import { useState, useEffect } from 'react'
import { FileText, Users, Building, MoreHorizontal, LayoutGrid, EyeIcon, EyeOffIcon, ClipboardPenLine } from 'lucide-react'
import { useLocation, useNavigate } from 'react-router-dom';
import { authService } from '../services/auth.service';
import { getMenuAccess } from '../config/roleAccess';
import Dialog from '../components/Dialog';
import { toast } from 'react-toastify';
import { useNetworkStatus } from '../hooks/useNetworkStatus';

interface SidebarProps {
  isOpen: boolean;
  onToggle?: () => void;
}

const SidebarItem = ({ icon, text, isOpen, active = false }: { icon: React.ReactNode; text: string; isOpen: boolean; active?: boolean }) => (
  <div className="transition-all duration-300 ease-in-out">
    <div className={`flex items-center px-3 py-3 mb-2 mx-2 rounded-md ${active ? 'bg-[#FFE9E8] text-[#A42823] cursor-pointer' : 'text-gray-700 hover:bg-[#FFE9E8] hover:text-[#A42823] cursor-pointer'} ${isOpen ? '' : 'justify-center'} transition-all duration-300 ease-in-out`}
      data-testid={`sidebar-item-${text.toLowerCase().replace(/\s+/g, '-')}`}
      data-active={active}
    >
      <div className="">{icon}</div>
      {isOpen && <span className="ml-3 text-[16px] font-normal leading-none">{text}</span>}
    </div>
  </div>
)

const UserInfo = ({ user, isOpen }: { user: any; isOpen: boolean }) => {
  const [showOptions, setShowOptions] = useState(false);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false
  });
  const [errors, setErrors] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  // Password validation function
  const validatePasswordForm = () => {
    let isValid = true;
    const newErrors = {
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    };

    // Current password validation
    if (!passwordForm.currentPassword.trim()) {
      newErrors.currentPassword = 'Current password is required';
      isValid = false;
    }

    // New password validation
    if (!passwordForm.newPassword.trim()) {
      newErrors.newPassword = 'New password is required';
      isValid = false;
    } else {
      // Password requirements regex checks
      const hasUpperCase = /[A-Z]/.test(passwordForm.newPassword);
      const hasLowerCase = /[a-z]/.test(passwordForm.newPassword);
      const hasNumber = /\d/.test(passwordForm.newPassword);
      const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(passwordForm.newPassword);

      if (!hasUpperCase || !hasLowerCase || !hasNumber || !hasSpecialChar || passwordForm.newPassword.length < 8) {
        newErrors.newPassword = 'Passwords must be at least 8 characters long and contain at least one uppercase letter, one number, and one special character';
        isValid = false;
      }
    }

    // Confirm password validation
    if (!passwordForm.confirmPassword.trim()) {
      newErrors.confirmPassword = 'Please confirm your new password';
      isValid = false;
    } else if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  // Password change handler
  const handlePasswordChange = async () => {
    if (validatePasswordForm()) {
      try {
        const currentUser = await authService.getUser();

        if (!currentUser?.id) {
          throw new Error('User not found');
        }

        await authService.changePassword({
          id: currentUser.id,
          currentPassword: passwordForm.currentPassword,
          newPassword: passwordForm.newPassword,
          confirmPassword: passwordForm.confirmPassword
        });

        setIsChangePasswordOpen(false);
        // Reset form and states
        setPasswordForm({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        setShowPasswords({
          currentPassword: false,
          newPassword: false,
          confirmPassword: false
        });
        setErrors({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        
        // Add success toast
        toast.success('Password changed successfully');
      } catch (error: any) {
        console.error('Password change error:', error);
        const errorMessage = error?.message || 'Failed to change password';
        setErrors(prev => ({
          ...prev,
          currentPassword: errorMessage
        }));
        // Add error toast
        toast.error(errorMessage);
      }
    }
  };

  // Password form input handler
  const handlePasswordFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordForm(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  if (!isOpen) return null;
  
  // Handle the case where user data is not accessible
  if (!user) return null;
  
  return (
    <div className="border-b border-border">
      <div className="px-4 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold text-gray-900">{user?.name || 'User'}</h2>
            <p className="text-sm text-gray-500">{user?.role === 'admin' ? 'Admin' : user?.role === 'organizer' ? 'Organizer' : user?.role === 'interRepre' ? 'International Representative' : 'Role'}</p>
          </div>
          <button 
            className="p-1 hover:bg-gray-100 rounded-full"
            onClick={() => setShowOptions(!showOptions)}
            aria-label="User options"
          >
            <MoreHorizontal size={20} className="text-gray-500" />
          </button>
        </div>
      </div>
      
      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${showOptions ? 'max-h-28' : 'max-h-0'}`}>
        <div className="px-4 py-3 border-t border-border">
          <button 
            className="w-full text-right text-gray-700 hover:text-[#A42823] py-2 transition-colors duration-200"
            onClick={() => setIsChangePasswordOpen(true)}
          >
            Change Password
          </button>
          <button 
            className="w-full text-right text-gray-700 hover:text-[#A42823] py-2 transition-colors duration-200"
            onClick={() => authService.logout()}
          >
            Log out
          </button>
        </div>
      </div>

      <Dialog
        isOpen={isChangePasswordOpen}
        onClose={() => {
          setIsChangePasswordOpen(false);
          setPasswordForm({
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
          });
          // Reset password visibility
          setShowPasswords({
            currentPassword: false,
            newPassword: false,
            confirmPassword: false
          });
          // Reset errors
          setErrors({
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
          });
        }}
        title="Change Password"
        id="change-password-dialog"
        width="w-[95%] sm:w-[600px] md:w-[700px] lg:w-[500px]"
        padding="px-3 py-4 sm:px-4 sm:py-5 md:px-10 md:py-8 lg:px-6 lg:py-6"
      >
        <div className="max-h-[80vh] sm:max-h-[70vh] overflow-y-auto scrollbar-hide">
          <div className="mt-4 sm:mt-6">
            <div className="space-y-4">
              <div className="">
                <label className="block text-[14px] font-medium text-[#767A81] mb-1">
                  Current Password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    id="current-password-input"
                    type={showPasswords.currentPassword ? 'text' : 'password'}
                    name="currentPassword"
                    value={passwordForm.currentPassword}
                    onChange={handlePasswordFormChange}
                    className={`w-full px-3 py-2 text-[16px] border ${errors.currentPassword ? 'border-red-500' : 'border-border'} rounded-input focus:outline-none focus:ring-1 focus:ring-black focus:border-black focus:ring-opacity-0`}
                    placeholder="Enter Current Password"
                  />
                  <button
                    id="toggle-current-password-visibility"
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPasswords(prev => ({
                      ...prev,
                      currentPassword: !prev.currentPassword
                    }))}
                    data-testid="toggle-password-visibility"
                  >
                    {showPasswords.currentPassword ? (
                      <EyeOffIcon className="h-5 w-5 text-gray-400" />
                    ) : (
                      <EyeIcon className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
                {errors.currentPassword && (
                  <p id="current-password-error" className="text-small text-red-600 text-right">{errors.currentPassword}</p>
                )}
              </div>

              <div className="">
                <label className="block text-[14px] font-medium text-[#767A81] mb-1">
                  New Password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.newPassword ? 'text' : 'password'}
                    name="newPassword"
                    value={passwordForm.newPassword}
                    onChange={handlePasswordFormChange}
                    className={`w-full px-3 py-2 text-[16px] border ${errors.newPassword ? 'border-red-500' : 'border-border'} rounded-input focus:outline-none focus:ring-1 focus:ring-black focus:border-black focus:ring-opacity-0`}
                    placeholder="Enter New Password"
                    id="new-password-input"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPasswords(prev => ({
                      ...prev,
                      newPassword: !prev.newPassword
                    }))}
                    data-testid="toggle-password-visibility"
                  >
                    {showPasswords.newPassword ? (
                      <EyeOffIcon className="h-5 w-5 text-gray-400" />
                    ) : (
                      <EyeIcon className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
                {errors.newPassword && (
                  <p className=" text-small text-red-600 text-right">{errors.newPassword}</p>
                )}
              </div>

              <div className="">
                <label className="block text-[14px] font-medium text-[#767A81] mb-1">
                  Confirm New Password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.confirmPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    value={passwordForm.confirmPassword}
                    onChange={handlePasswordFormChange}
                    className={`w-full px-3 py-2 text-[16px] border ${errors.confirmPassword ? 'border-red-500' : 'border-border'} rounded-input focus:outline-none focus:ring-1 focus:ring-black focus:border-black focus:ring-opacity-0`}
                    placeholder="Confirm New Password"
                    id="confirm-password-input"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPasswords(prev => ({
                      ...prev,
                      confirmPassword: !prev.confirmPassword
                    }))}
                    data-testid="toggle-password-visibility"
                  >
                    {showPasswords.confirmPassword ? (
                      <EyeOffIcon className="h-5 w-5 text-gray-400" />
                    ) : (
                      <EyeIcon className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className="text-small text-red-600 text-right">{errors.confirmPassword}</p>
                )}
              </div>
            </div>
          </div>

          <div className="sticky bottom-0 bg-white pt-8 mt-8">
            <button
              id="submit-password-change"
              onClick={handlePasswordChange}
              className="btn-primary w-full"
            >
              Change Password
            </button>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default function Sidebar({ isOpen, onToggle }: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [menuAccess, setMenuAccess] = useState(getMenuAccess('')); // Default empty role
  const [user, setUser] = useState<any>(null);
  const { isOnline } = useNetworkStatus();

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const userData = await authService.getUser();
        setUser(userData);
        setMenuAccess(getMenuAccess(userData?.role || ''));
      } catch (error: any) {
        // If there's a permission error, just use the role we have
        if (error.message?.includes('Access denied')) {
          const role = localStorage.getItem('userRole');
          if (role) {
            setMenuAccess(getMenuAccess(role));
          }
        }
        console.error('Error fetching user:', error);
      }
    };

    fetchUser();
  }, []);

  const handleMenuClick = (path: string) => {
    // Reset jobsite view mode to list when navigating away from jobsite management
    const isFromJobsiteManagement = location.pathname.includes('/jobsite-management');
    const isToJobsiteManagement = path.includes('/jobsite-management');
    
    if (isFromJobsiteManagement && !isToJobsiteManagement) {
      // Reset to list view when navigating away from jobsite management
      sessionStorage.setItem('jobsiteViewMode', 'list');
    }
    
    navigate(path);
    if (window.innerWidth <= 1194 && onToggle) {
      onToggle();
    }
  };

  const onlineMenuItems = [
    {
      path: '/dashboard',
      text: 'Dashboard',
      icon: <div data-testid="dashboard-icon"><LayoutGrid size={20} /></div>,
      access: menuAccess.dashboard,
    },
    {
      path: '/user-management',
      text: 'User Management',
      icon: <div data-testid="user-icon"><Users size={20} /></div>,
      access: menuAccess.userManagement,
    },
    {
      path: '/jobsite-management',
      text: 'Jobsite Management',
      icon: <div data-testid="jobsite-icon"><Building size={20} /></div>,
      access: menuAccess.jobsiteManagement,
    },
    {
      path: '/reports',
      text: 'Reports',
      icon: <div data-testid="reports-icon"><FileText size={20} /></div>,
      access: menuAccess.reports,
    },
    {
      path: '/general-contractor-management',
      text: 'General Contractor Management',
      icon: <div data-testid="building-icon"><Building size={20} /></div>,
      access: menuAccess.generalContractorManagement,
    },
    {
      path: '/mechanical-contractor-management',
      text: 'Mechanical Contractor Management',
      icon: <div data-testid="mechanical-contractor-icon"><Building size={20} /></div>,
      access: menuAccess.contractorManagement, // Use same access as contractor management for now
    },
    {
      path: '/jobsite-visits',
      text: 'Jobsite Visits',
      icon: <div data-testid="user-icon"><ClipboardPenLine size={20} /></div>,
      access: menuAccess.jobsiteVisits,
    },
  ];

  const offlineMenuItems = [
    {
      path: '/jobsite-management',
      text: 'Jobsite Management',
      icon: <div data-testid="jobsite-icon"><Building size={20} /></div>,
      access: menuAccess.jobsiteManagement,
    }
  ];

  const menuItems = isOnline ? onlineMenuItems : offlineMenuItems;


  return (
    <div className={`bg-white h-[calc(100vh-64px)] fixed top-16 left-0 transition-all duration-300 ease-in-out ${isOpen ? 'w-64' : 'w-0'} shadow-lg overflow-hidden z-[9]`} data-testid="sidebar">
      <UserInfo user={user} isOpen={isOpen} />
      <nav className="mt-4">
        {isOpen && menuItems.map((item) => 
          item.access && (
            <div key={item.path} onClick={() => handleMenuClick(item.path)}>
              <SidebarItem
                icon={item.icon}
                text={item.text}
                isOpen={isOpen}
                active={location.pathname === item.path || location.pathname.startsWith(`${item.path}/`)}
                // active={item.path === '/user-management' && (location.pathname === '/user-management' || location.pathname.startsWith('/user-management/'))}
              />
            </div>
          )
        )}
      </nav>
    </div>
  )
}
